import { ForecastSlice, useCurrentMarketLocation, useForecastData, useSnackbar } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, parseNumber } from '@e2m-customer-portal/shared';
import { AbortConfirmationDialog, ErrorComponent } from '@e2m-customer-portal/ui';
import { eachDayOfInterval, getTime, Interval, setHours } from 'date-fns';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ForecastProperty, ForecastSliceInput, Resolution } from '../../../../generated/graphql';
import { getEarliestMarketingDeadline, getMarketingDeadline } from '../../../../utils';
import { getErrorTranslationKey } from '../../../../utils/error-translation-key';
import { useForecastDataMutation } from '../../short-term-forecast/forecast-list/forecast-table/queries';
import { SaveBar } from '../../short-term-forecast/save-bar/SaveBar';
import { DeviceWithInstalledPower } from '../ControlEnergyForecast';
import { ControlDeviceTable } from './control-forecast-table/ControlDeviceTable';
import { ProductInput } from './control-forecast-table/utils';

interface ControlForecastListProps {
  /**
   * All potential devices to display in the table. Their forecast data will be loaded.
   */
  devices: readonly DeviceWithInstalledPower[];

  /**
   * The selected devices to display in the table. They will be sorted alphabetically
   * by name. If not given, all devices will be displayed.
   */
  shownDevices?: readonly DeviceWithInstalledPower[];

  /**
   * The start and end date of the displayed values.
   */
  interval: Interval;

  /**
   * Flag to indicate whether the editing status is active
   */
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  currentForecastProperty?: ForecastProperty;
}

export function ControlForecastList({
  devices,
  shownDevices,
  interval,
  setIsEditing,
  currentForecastProperty,
}: Readonly<ControlForecastListProps>): ReactElement<ControlForecastListProps> {
  const [t] = useTranslation();

  const marketLocation = useCurrentMarketLocation();
  const [forecasts, error] = useForecastData(
    devices.map((device) => device.id),
    interval,
    Resolution.FOUR_HOURS,
  );

  const daysInForecast = eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end));

  const deadlineDates = new Map<string, Date>();
  const slices = new Map<string, readonly ForecastSlice[]>();
  const defaultOverviewMap = new Map<string, ProductInput[][][]>();

  for (const device of devices) {
    const forecast = forecasts?.find((f) => f.deviceId === device.id);
    if (!forecast) {
      throw new TypeError(`Data Error: Control energy forecast for device with id "${device.id}" not found`);
    }

    const deadlineDate = device.deadlines.length
      ? getMarketingDeadline(getEarliestMarketingDeadline(device.deadlines))
      : undefined;
    if (deadlineDate) {
      deadlineDates.set(device.id, deadlineDate);
    }

    const sortedSlices = [...forecast.slices].sort((a, b) => a.date.getHours() - b.date.getHours());

    slices.set(device.id, sortedSlices);

    const overviewMap = new Array<Array<Array<ProductInput>>>();
    for (let row = 0; row < 6; row++) {
      const columns = new Array<Array<ProductInput>>();
      daysInForecast.forEach(() => columns.push(new Array<ProductInput>()));
      overviewMap.push(columns);
    }

    defaultOverviewMap.set(device.id, overviewMap);
  }

  const [overviewMap, setOverviewMap] = useState(defaultOverviewMap);

  const [resetTrigger, setResetTrigger] = useState(false);
  const [editedSliceProducts, setEditedSliceProducts] = useState(new Map<string, Set<InputFieldType>>());
  const [editedDevices, setEditedDevices] = useState<string[]>([]);
  const [erroneousSliceProducts, setErroneousSliceProducts] = useState(new Map<string, Set<InputFieldType>>());

  const changesToBeSaved = () => {
    const changesToBeSaved = new Map<string, Array<ForecastSlice>>();

    slices.forEach((deviceSlices, deviceId) => {
      changesToBeSaved.set(
        deviceId,
        deviceSlices.filter((slice) => editedSliceProducts.has(slice.id)),
      );
    });

    return changesToBeSaved;
  };

  const [update, { loading }] = useForecastDataMutation();
  const { showError, showSuccess } = useSnackbar();

  const saveChanges = () => {
    if (erroneousSliceProducts.size) return;

    changesToBeSaved().forEach(async (deviceSlices, deviceId) => {
      const deviceSliceInputs = new Array<ForecastSliceInput>();
      const allDeviceSlices = slices.get(deviceId)!;

      for (const slice of deviceSlices) {
        const deviceMap = overviewMap.get(deviceId);
        if (!deviceMap) throw new TypeError(`Data Error: Table overview for device with id "${deviceId}" not found`);

        const sliceIndex = allDeviceSlices.findIndex((otherSlice) => slice.id === otherSlice.id) ?? -1;
        if (sliceIndex === -1)
          throw new TypeError(`Data Error: Slice with id "${slice.id}" not found in device with id "${deviceId}"`);

        // To prevent DST errors, we round the four-hour slots.
        // This turns 03:00 (0.75) and 05:00 (1.25) into 1 (04:00).
        // Since 23 would turn into 24, we let Date turn it into a 0 for us.
        const fields = deviceMap[setHours(slice.date, Math.round(slice.date.getHours() / 4) * 4).getHours() / 4][
          sliceIndex % daysInForecast.length
        ]
          .map((product) => ({
            type: product.product,
            watt: convertMeasure({ value: parseNumber(product.input?.value), unit: 'kW' }, 'W').value,
          }))
          .filter((field) => editedSliceProducts.get(slice.id)?.has(field.type));

        deviceSliceInputs.push({
          date: slice.date,
          fields,
          hasDowntime: false,
        });
      }

      if (deviceSliceInputs.length) {
        try {
          await update(deviceId, interval, deviceSliceInputs, Resolution.FOUR_HOURS);
          showSuccess(t('FORECAST_WIZARD_SUCCESS'));
          setEditedSliceProducts(new Map<string, Set<InputFieldType>>());
        } catch (error: unknown) {
          showError(t(getErrorTranslationKey(error)));
          console.error(error);
        }
      }
    });
  };

  useEffect(() => {
    if (!!editedSliceProducts.size || !!erroneousSliceProducts.size) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSliceProducts.size, erroneousSliceProducts.size]);

  useEffect(() => {
    slices.forEach((deviceSlices, deviceId) => {
      if (!editedDevices.includes(deviceId) && deviceSlices.some((slice) => editedSliceProducts.has(slice.id))) {
        setEditedDevices([...editedDevices, deviceId]);
      } else if (editedDevices.includes(deviceId) && !deviceSlices.some((slice) => editedSliceProducts.has(slice.id))) {
        setEditedDevices(editedDevices.filter((id) => id !== deviceId));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSliceProducts.size]);

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return (
    <div data-testid={`device-list-${marketLocation.id}`} className="last-of-type:pb-[85px]">
      {(shownDevices ?? devices)?.map((device, index) => (
        <ControlDeviceTable
          marketLocation={marketLocation}
          key={`device-table-${device.id}`}
          deviceIndex={index}
          device={device}
          deadlineDate={deadlineDates.get(device.id)}
          slices={slices.get(device.id) ?? []}
          interval={interval}
          daysInForecast={daysInForecast}
          readOnly={!marketLocation.permissions.editForecast}
          resetTrigger={resetTrigger}
          isBeingEdited={!!editedSliceProducts.size || !!erroneousSliceProducts.size}
          overviewMap={overviewMap}
          editedSliceProducts={editedSliceProducts}
          editedDevices={editedDevices}
          setOverviewMap={setOverviewMap}
          setEditedSliceProducts={setEditedSliceProducts}
          setErroneousSliceProducts={setErroneousSliceProducts}
          saveChanges={saveChanges}
          currentForecastProperty={currentForecastProperty}
        />
      ))}
      <SaveBar
        open={!!editedSliceProducts.size || !!erroneousSliceProducts.size}
        saving={loading}
        isValid={!erroneousSliceProducts.size}
        onCancel={() => setResetTrigger(!resetTrigger)}
        onSave={saveChanges}
      />
      <AbortConfirmationDialog when={!!editedSliceProducts.size || !!erroneousSliceProducts.size} />
    </div>
  );
}
